
/**
 * Dropdown component.
 *
 * How to use it:
 *
 *     <input type="hidden" id="dropdown-value">
 *     <div id="dropdown"></div>
 *     <template type="text/template" id="dropdown-selected-item">
 *       <div><%= item.name %> (age: <%= item.age %>)</div>
 *     </template>
 *     <template type="text/template" id="dropdown-list-item">
 *       <div sd-dd-value="<%= item.id %>">
 *         <%= item.name %> / <%= item.age %>
 *       </div>
 *     </template>
 *
 *     const dropdown = new Dropdown('#dropdown", {
 *       data() {
 *         return [
 *           { id: 1, name: 'John', age: 18 },
 *           { id: 2, name: 'Kate', age: 20 },
 *           { id: 3, name: 'Josh', age: 6 }
 *         ];
 *       },
 *       // Init value.
 *       value: 2,
 *       // Input where store selected value.
 *       position: 'left/right',
 *       width: '100%',
 *       valueInput: '#dropdown-value',
 *       renderSelectedItem: '#dropdown-selected-item',
 *       renderListItem: '#dropdown-list-item',
 *     });
 *
 *     dropdown.on('valueChange', (dataItem) => {
 *        // react on value selection
 *     });
 *
 * Available configs:
 *
 * * `data` _{Function}_ Function to load data later used to render items list. This function
 *    should return array or Promise.
 * * `renderSelectedItem` _{Function/String/Null/Boolean/undefined}_ Function which return html or
 *    selector to DOM template. It also can be "null" then after clicking on item no
 *    rendering will be fired (but "valueChange" event will be emitted nevertheless).
 * * `renderListItem` _{Function/String}_ Function which return html or selector to
 *    DOM template.
 * * `valueInput` _{String}_ Selector to input where selected value should be stored.
 * * `position` _{String}_ Set position of list items.
 * * `width` _{String}_ Set width of list items.
 *
 * @author Tomasz Zeludziewicz @ 2020/08/10
 */

import Debounce from './utils/debounce'
import Template from './utils/template'

export default class Dropdown {
  constructor(el, o) {
    this.listeners = {};
    /**
     * Dropdown main element. This element shows selected value and activates
     * showing items.
     * @property {Node} el
     * @readonly
     */
    this.el = typeof el === 'string' ? document.querySelectordocument.querySelector(el) : el;
    if (!(this.el instanceof Node)) {
      throw TypeError('[SD Dropdown] "el" config must be valid selector or instance of Node class.');
    }
    if (o.valueInput) {
      /**
       * Input where dropdown value will be stored.
       * @property {Node} valueInput
       * @readonly
       */
      this.valueInput = typeof o.valueInput === 'string' ? document.querySelector(o.valueInput) : o.valueInput;
      if (!(this.valueInput instanceof Node)) {
        throw TypeError('[SD Dropdown] "valueInput" config must be valid selector or instance of Node class.');
      }
    }

    /**
     * Resolve if items element is visible.
     * @property {boolean} itemsVisible
     * @readonly
     */
    this.itemsVisible = false;
    /**
     * Resolve if mouse is over items element.
     * @property {boolean} enteredItems
     * @private
     */
    this.enteredItems = false;

    if (typeof o.data !== 'function') {
      throw TypeError('[SD Dropdown] "data" config is required and must be type of function.');
    }
    /**
     * Stores cached result of getData method.
     * @property {null/Array}
     * @private
     */
    this.data = null;
    this.getData = () => {
      if (this.data !== null) {
        return Promise.resolve(this.data);
      }
      const result = o.data();
      if (!(result instanceof Promise)) {
        this.data = result;
        return Promise.resolve(result);
      }
      result.then((data) => {
        this.data = data;
      });
      return result;
    };

    if (!o.renderListItem) {
      throw TypeError('[SD Dropdown] renderListItem property is required.');
    }

    const template = new Template(o.renderListItem, null, 'item');

    this.renderListItem =
      typeof o.renderListItem === 'string' ?
        template.build() :
        (o) => o.renderListItem(o.item);

    // renderSelectedItem may be null, then no rendering of selected item will have place.
    this.renderSelectedItem =
      !o.renderSelectedItem ?
        null :
        (
          typeof o.renderSelectedItem === 'string' ?
            template.build() :
            (o) => o.renderSelectedItem(o.item)
        );

    if (o.value) {
      this.value = o.value;
      this.selectItem(o.value);
    }

    this.position = o.position;
    this.width = o.width;

    // When dropdown arrow element is clicked, show items list.
    this.el.querySelector('.sd-btn__expander').addEventListener('click', () => {
      this.open();
    });

    // Update position of items while scrolling or resizing window.
    const updateItemsPosition = () => {
      if (this.itemsVisible) {
        this.updateItemsElPosition();
      }
    };

    const mainArea = document.querySelector('#mainArea')
    mainArea && mainArea.addEventListener('scroll', updateItemsPosition);

    const debounce = new Debounce(updateItemsPosition, 100);

    window.addEventListener('resize', debounce.call());
  }

  on(eventName, callback) {
    const listeners = this.listeners;

    if (!listeners.hasOwnProperty(eventName)) {
      listeners[eventName] = [];
    }

    listeners[eventName].push({
      event: eventName,
      fn: callback
    })

    return this;
  }

  emit(eventName, eventObject = {}) {
    const
      listeners = this.listeners[eventName] || [],
      len = listeners.length;

    for (let i = 0; i < len; i++) {
      listeners[i].fn(eventObject, this);
    }

    return this;
  }

  /**
   * Create items list element.
   * @return void
   */
  createItemsEl() {
    const el = document.createElement('div');
    el.classList.add('sd-dropdown__items');
    el.classList.add('sd-dropdown--hidden');
    // @todo Most of this can be moved to css.
    el.style.position = 'fixed';
    el.style.top = '0';
    el.style.left = '0';
    el.style.zIndex = 1000;
    el.style.width = (this.width || this.el.offsetWidth) + 'px';
    document.body.appendChild(el);

    // @todo getData could be loading data using ajax. There
    // should be method like "renderItemsList" which would
    // be called twice (with and without) items - which would
    // allow to render loading message.
    this.getData().then((items) => {
      const html = [];
      items.forEach((item) => {
        html.push(this.renderListItem(item));
      });
      el.innerHTML = html.join('');
    });

    // Hide items when mouse leaves it.
    el.addEventListener('mouseenter', () => {
      if (!this.enteredItems && this.itemsVisible) {
        this.enteredItems = true;
      }
    });
    el.addEventListener('mouseleave', () => {
      if (this.enteredItems && this.itemsVisible) {
        this.hideItems();
      }
    });
    // Hide dropdown items when user clicks outside of the dropdown area.
    document.addEventListener('click', ( evt ) => {
      if (!evt.target.closest('.sd-dropdown__items')) {
        this.hideItems();
      }
    });
    // When item is clicked select it.
    el.addEventListener('click', (e) => {
      const itemEl = e.target.closest('[sd-dd-value]');
      if (itemEl) {
        this.selectItem(itemEl.getAttribute('sd-dd-value'));
        this.hideItems();
      }
    });

    this.itemsEl = el;
  }

  /**
   * Return full height of the element (with margin, padding, border etc).
   * @param {Node} el
   * @returns {number}
   */
  getHeight(el) {
    const
      styles = window.getComputedStyle(el),
      margin = parseFloat(styles.marginTop) + parseFloat(styles.marginBottom);

    return Math.ceil(el.offsetHeight + margin);
  }

  /**
   * Return item by id.
   *
   *     dropdown.getItem(3).then((item) => {
   *       console.log(item.name);
   *     });
   *
   * @param {String/Number} id Id of the item.
   * @returns {Promise}
   */
  getItem(id) {
    return new Promise((resolve, reject) => {
      this.getData().then((items) => {
        let found = false;
        items.forEach((item) => {
          if (item.id == id) {
            resolve(item);
            found = true;
          }
        })
        if (!found) {
          reject();
        }
      })
    });
  }

  getItemsEl() {
    if (!this.itemsEl) {
      this.createItemsEl();
    }

    return this.itemsEl;
  }

  open() {
    const itemsEl = this.getItemsEl();

    this.enteredItems = false;
    this.updateItemsElPosition();
    itemsEl.classList.remove('sd-dropdown--hidden');
    // I know setTimeouts are disgusting but there's no time for fancy coding.
    setTimeout(() => {
      itemsEl.classList.add('sd-dropdown--visible');
      setTimeout(() => { this.itemsVisible = true; }, 250);
    }, 50);
  }

  hideItems() {
    if (this.itemsVisible) {
      this.itemsVisible = false;
      this.itemsEl.classList.remove('sd-dropdown--visible');
      this.itemsEl.classList.add('sd-dropdown--hidden');
    }
  }

  updateItemsElPosition() {
    const
      el = this.el,
      pos = el.getBoundingClientRect(),
      itemsEl = this.getItemsEl();

    itemsEl.style.top = (this.getHeight(el) + pos.top) + 'px';
    if (this.position === 'right') {
      itemsEl.style.right = (window.innerWidth - pos.right) - 2 + 'px';
      itemsEl.style.left = 'auto';
    } else {
      itemsEl.style.left = pos.left + 'px';
    }
  }

  /**
   * Select item.
   * @param {String/Number} id Id of the item to select.
   * @return void
   */
  selectItem(id) {
    this.getItem(id).then((item) => {
      if (this.renderSelectedItem) {
        this.el.innerHTML = this.renderSelectedItem(item);
      }
      if (this.valueInput) {
        this.valueInput.value = item.id;
      }
      this.emit('valueChange', { value: item });
    });
  }
}


